import { useMemo } from 'react'
import * as THREE from 'three'

/** Viewer: fog hides the horizon on white. Export: tight fade + alpha discard for transparent PNGs. */
const PRESETS = {
  viewer: { fadeInner: 12, fadeOuter: 38, planeSize: 100 },
  export: { fadeInner: 6, fadeOuter: 20, planeSize: 44 },
} as const

type ShadowFloorVariant = keyof typeof PRESETS

function applyGroundFade(
  material: THREE.ShadowMaterial,
  fadeInner: number,
  fadeOuter: number,
  cacheKey: string,
) {
  material.customProgramCacheKey = () => cacheKey
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uFadeInner = { value: fadeInner }
    shader.uniforms.uFadeOuter = { value: fadeOuter }

    shader.vertexShader = `
varying vec2 vGroundXZ;
${shader.vertexShader}
`.replace(
      '#include <worldpos_vertex>',
      `#include <worldpos_vertex>
vGroundXZ = worldPosition.xz;`,
    )

    const groundFadeSnippet = `
{
  float groundDist = length(vGroundXZ);
  float groundFade = 1.0 - smoothstep(uFadeInner, uFadeOuter, groundDist);
  gl_FragColor.a *= groundFade * groundFade;
  if (gl_FragColor.a < 0.02) discard;
}`

    shader.fragmentShader = `
uniform float uFadeInner;
uniform float uFadeOuter;
varying vec2 vGroundXZ;
${shader.fragmentShader}
`.replace(
      'gl_FragColor = vec4( color, opacity * ( 1.0 - getShadowMask() ) );',
      `gl_FragColor = vec4( color, opacity * ( 1.0 - getShadowMask() ) );${groundFadeSnippet}`,
    )
  }
}

type ShadowFloorProps = {
  disabled: boolean
  opacity?: number
  /** `export` = transparent PNG /screenshot (tight fade, no visible plane edge). */
  variant?: ShadowFloorVariant
}

export default function ShadowFloor({
  disabled,
  opacity = 0.3,
  variant = 'viewer',
}: ShadowFloorProps) {
  const preset = PRESETS[variant]

  const material = useMemo(() => {
    const mat = new THREE.ShadowMaterial({
      transparent: true,
      opacity: disabled ? 0 : opacity,
      color: '#000000',
      fog: variant === 'viewer',
    })
    applyGroundFade(mat, preset.fadeInner, preset.fadeOuter, `shadow-floor-fade-${variant}`)
    return mat
  }, [disabled, opacity, variant, preset.fadeInner, preset.fadeOuter])

  material.opacity = disabled ? 0 : opacity

  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, -0.001, 0]}
      receiveShadow={!disabled}
    >
      <planeGeometry args={[preset.planeSize, preset.planeSize]} />
      <primitive object={material} attach="material" />
    </mesh>
  )
}
